import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  deleteEmployeeLoyalty,
  getEmployeeLoyalty,
  saveEmployeeLoyalty,
} from '../../../../api/endpoints/corporateEmployee';
import type { CorporateLoyalty } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';

const FIELDS: { name: keyof CorporateLoyalty; label: string }[] = [
  { name: 'programType', label: 'Program (Havayolu / Otel)' },
  { name: 'cardNo', label: 'Kart No' },
  { name: 'documentFile', label: 'Belge Dosyası' },
];

/** Sadakat programı (frequent-flyer / otel kart) CRUD — `CorporateLoyalty/*` (backend Faz F). */
export function LoyaltySection({ employeeID }: { employeeID: number }) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState<CorporateLoyalty | null>(null);
  const [form, setForm] = useState<Partial<CorporateLoyalty>>({});
  const [deleting, setDeleting] = useState<CorporateLoyalty | null>(null);

  const query = useQuery({
    queryKey: ['corporateEmployee', 'loyalty', employeeID],
    queryFn: () => getEmployeeLoyalty(employeeID),
  });
  const rows: CorporateLoyalty[] = query.data?.corporateLoyalty ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['corporateEmployee', 'loyalty', employeeID] });

  const saveMutation = useMutation({
    mutationFn: () => saveEmployeeLoyalty({ ...editing, ...form, employeeID }),
    onSuccess: () => {
      enqueueSnackbar('Sadakat kaydı kaydedildi.', { variant: 'success' });
      setEditing(null);
      invalidate();
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteEmployeeLoyalty({ loyaltyProgramID: deleting?.loyaltyProgramID, employeeID }),
    onSuccess: () => {
      enqueueSnackbar('Sadakat kaydı silindi.', { variant: 'success' });
      setDeleting(null);
      invalidate();
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  const open = (row: CorporateLoyalty | null) => {
    setEditing(row ?? { employeeID });
    setForm(row ?? {});
  };

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1">Sadakat Programları</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => open(null)}>
          Yeni Kayıt
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Program</TableCell>
              <TableCell>Kart No</TableCell>
              <TableCell>Belge</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>
                    Kayıtlı sadakat programı bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.loyaltyProgramID} hover>
                <TableCell>{row.programType ?? '—'}</TableCell>
                <TableCell>{row.cardNo ?? '—'}</TableCell>
                <TableCell>{row.documentFile ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => open(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing?.loyaltyProgramID ? 'Kaydı Düzenle' : 'Yeni Sadakat Kaydı'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {FIELDS.map((field) => (
              <TextField
                key={field.name}
                size="small"
                label={field.label}
                value={(form[field.name] as string) ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, [field.name]: e.target.value }))}
              />
            ))}
            <ApiErrorAlert error={saveMutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saveMutation.isPending}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        title="Sadakat kaydını sil"
        description={`"${deleting?.programType ?? ''}" kaydını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
